import React, { useState } from "react"
import { handleRequest } from "../FuncionRequest/FuncionRequest"
import {
  Alert,
  Box,
  Button,
  Collapse,
  IconButton,
  InputAdornment,
  TextField,
} from "@mui/material"
import { Visibility, VisibilityOff } from "@mui/icons-material"
import ComoFunc from "../ComoFunc.tsx/ComoFunc"
import CloseIcon from "@mui/icons-material/Close"
import { encryptText } from "../../utils/claveAndEncryptUtils"
import jsPDF from "jspdf"
import { toPng } from "html-to-image"
import { useSpinner } from "../../contexts/SpinnerProvider"
import "./GeneradorQR.css"
import { capitalizeNombresPropios } from "../../utils/StringUtils"
import { QrData, QR_CONFIGS } from "./qrConfigs"
import GenericQRCard from "./GenericQRCard"
import FiestaQRCard from "./FiestaQRCard"

interface QrValidationResponse {
  message: string
  nombre: string
  apellido: string
  tarjetasPagadas: string
  tarjetasPagadasMenores: string
  fechaEvento: string
  tipoEvento?: string
  categoria?: string
  status?: number
}

const GeneradorQR = () => {
  const [dni, setDni] = useState("")
  const [clave, setClave] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [isValid, setIsValid] = useState(false)
  const [qrDataObj, setQrDataObj] = useState<QrData | null>(null)
  const [mensajeAlert, setMensajeAlert] = useState<React.ReactNode>(undefined)
  const [tipoAlert, setTipoAlert] = useState<"error" | "warning">("error")
  const { addLoading, removeLoading } = useSpinner()
  const API_URL = process.env.REACT_APP_API_SERVER_URL_VALID || ""

  const handleMensajeAlert = (
    mensaje: React.ReactNode = undefined,
    tipo: "error" | "warning" = "error",
  ) => {
    setMensajeAlert(mensaje)
    setTipoAlert(tipo)
  }

  const handleGenerateQR = async () => {
    if (!dni || !clave) {
      handleMensajeAlert("Por favor, complete todos los campos.", "warning")
      return
    }

    try {
      const qrBase = `${dni}-${encryptText(clave)}`
      const response = await handleRequest<QrValidationResponse>(
        "POST",
        API_URL,
        { dni, clave },
      )
      if (!response) {
        handleMensajeAlert("Error al conectar con el servidor.", "error")
        setIsValid(false)
        return
      }

      if (response.message === "sin tarjetas") {
        handleMensajeAlert(
          <div>
            <span style={{ display: "inline-block" }}>
              No posee tarjetas para generar QR,{" "}
            </span>
            <span style={{ display: "inline-block" }}>
              comuníquese con administración.
            </span>
          </div>,
        )
        setIsValid(false)
        return
      }

      if (response.message === "Plazo expirado") {
        handleMensajeAlert(
          "La fecha de generación de QR para este evento ha expirado.",
        )
        setIsValid(false)
        return
      }

      if (response.message === "Validado correctamente") {
        handleMensajeAlert()
        setQrDataObj({
          qrBase,
          nombre: response.nombre,
          apellido: response.apellido,
          fechaEvento: response.fechaEvento,
          tarjetasPagadas: parseInt(response.tarjetasPagadas) || 0,
          tarjetasPagadasMenores:
            parseInt(response.tarjetasPagadasMenores) || 0,
          tipoEvento: response.tipoEvento,
          categoria: response.categoria,
          dni: parseInt(dni) || undefined,
        })
        setIsValid(true)
      } else if (response.status === 400) {
        setIsValid(false)
        handleMensajeAlert("DNI o clave incorrectos.", "warning")
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Error de validación:", err)
      setIsValid(false)
      handleMensajeAlert("DNI o clave incorrectos.", "warning")
    }
  }

  const handleDownloadPDF = async (): Promise<void> => {
    const contentElement = document.getElementById("printable-area")
    if (!contentElement || !qrDataObj) return

    try {
      addLoading()
      const dataUrl = await toPng(contentElement, {
        quality: 1,
        pixelRatio: 4,
        backgroundColor: "#ffffff",
      })
      if (!dataUrl) return

      const pdf = new jsPDF("p", "pt", "a4")
      const pdfW = pdf.internal.pageSize.width
      const pdfH = pdf.internal.pageSize.height
      const margin = 50
      const maxW = (pdfW - margin * 2) * 0.75
      const maxH = (pdfH - margin * 2) * 0.75
      const aspect = contentElement.clientHeight / contentElement.clientWidth
      const imgWidth = aspect * maxW > maxH ? maxH / aspect : maxW
      const imgHeight = imgWidth * aspect
      const x = (pdfW - imgWidth) / 2
      const y = 85

      pdf.setFillColor(255, 255, 255)
      pdf.rect(
        0,
        0,
        pdf.internal.pageSize.width,
        pdf.internal.pageSize.height,
        "F",
      )
      pdf.addImage(dataUrl, "PNG", x, y, imgWidth, imgHeight)
      pdf.save(
        `QR_${capitalizeNombresPropios(qrDataObj.apellido)}_${capitalizeNombresPropios(qrDataObj.nombre)}.pdf`,
      )
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error al generar el PDF:", error)
    } finally {
      removeLoading()
    }
  }

  const visibleConfigs = qrDataObj
    ? QR_CONFIGS.filter((c) => c.visibleSi(qrDataObj))
    : []

  return (
    <div style={{ backgroundImage: `url('/assets/img/Generador.jpg')` }}>
      <ComoFunc />
      <div>
        <div className="container text-center">
          <div
            className="card"
            style={{ maxWidth: "500px", margin: "auto", marginBottom: "50px" }}
          >
            {!!mensajeAlert && (
              <Collapse in={!!mensajeAlert}>
                <Alert
                  severity={tipoAlert}
                  variant="filled"
                  action={
                    <IconButton
                      aria-label="close"
                      color="inherit"
                      size="small"
                      onClick={() => handleMensajeAlert()}
                    >
                      <CloseIcon fontSize="inherit" />
                    </IconButton>
                  }
                  sx={{ mb: 2, textAlign: "center" }}
                >
                  {mensajeAlert}
                </Alert>
              </Collapse>
            )}
            <div className="card-body shadow-lg">
              <Box sx={{ p: 2 }}>
                <Box mb={2}>
                  <TextField
                    label="DNI"
                    type="number"
                    value={dni}
                    onChange={(e) => setDni(e.target.value)}
                    fullWidth
                    required
                  />
                </Box>
                <Box mb={2}>
                  <TextField
                    label="Contraseña"
                    type={showPassword ? "text" : "password"}
                    value={clave}
                    onChange={(e) => setClave(e.target.value)}
                    fullWidth
                    required
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    InputLabelProps={{ shrink: !!clave }}
                  />
                </Box>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleGenerateQR}
                >
                  Generar QR
                </Button>
              </Box>

              {isValid && qrDataObj && (
                <div className="qr-code mt-3 text-center">
                  <div
                    id="printable-area"
                    className="qr-code-container"
                    style={{ width: "360px", margin: "0 auto", padding: "0 40px", boxSizing: "content-box" }}
                  >
                    {visibleConfigs.map((config) =>
                      config.cardTemplate === "fiesta" ? (
                        <FiestaQRCard
                          key={config.id}
                          config={config}
                          data={qrDataObj}
                          cardWidth={208}
                          qrSize={108}
                        />
                      ) : (
                        <GenericQRCard
                          key={config.id}
                          config={config}
                          data={qrDataObj}
                          size={160}
                        />
                      ),
                    )}
                  </div>
                  <div className="d-flex flex-row justify-content-center gap-5">
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleDownloadPDF}
                    >
                      Descargar PDF
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default GeneradorQR
